import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TermsOfService = () => {
  const { t, i18n } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('termsOfService.backToHome')}
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('termsOfService.title')}</h1>
          <p className="text-gray-600">{t('termsOfService.lastUpdated')} {new Date().toLocaleDateString(i18n.language)}</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section1.title')}</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              {t('termsOfService.section1.content1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('termsOfService.section1.content2')}
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section2.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section2.subtitle1')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section2.list1', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section2.subtitle2')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section2.content2')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section2.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section3.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section3.subtitle1')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section3.content1')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section3.list1', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section3.subtitle2')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section3.content2')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section3.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-3 italic">{t('termsOfService.section3.note')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section3.subtitle3')}</h3>
            <p className="text-gray-700">{t('termsOfService.section3.content3')}</p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section4.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section4.subtitle1')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section4.list1', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section4.subtitle2')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section4.content2')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section4.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-red-600 font-semibold mt-3">{t('termsOfService.section4.warning')}</p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section5.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section5.subtitle1')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section5.content1')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section5.list1', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-2">{t('termsOfService.section5.declaration')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section5.subtitle2')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section5.content2')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section5.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>{t('termsOfService.section5.important')}</strong> {t('termsOfService.section5.importantText')}
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section5.subtitle3')}</h3>
            <p className="text-gray-700">{t('termsOfService.section5.content3')}</p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section6.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section6.subtitle1')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section6.list1', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section6.subtitle2')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section6.content2')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section6.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section6.subtitle3')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section6.list3', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section6.subtitle4')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section6.list4', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section7.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section7.subtitle1')}</h3>
            <p className="text-gray-700">{t('termsOfService.section7.content1')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section7.subtitle2')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section7.content2')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section7.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-2">{t('termsOfService.section7.warning')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section7.subtitle3')}</h3>
            <p className="text-gray-700">{t('termsOfService.section7.content3')}</p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section8.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section8.subtitle1')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section8.content1')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section8.list1', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section8.subtitle2')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section8.content2')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section8.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-3">{t('termsOfService.section8.limit')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section8.subtitle3')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section8.content3')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section8.list3', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section9.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section9.subtitle1')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section9.list1', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section9.subtitle2')}</h3>
            <p className="text-gray-700 mb-3">{t('termsOfService.section9.content2')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section9.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-3 italic">{t('termsOfService.section9.note')}</p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section10.title')}</h2>
            <p className="text-gray-700 mb-3">{t('termsOfService.section10.content')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('termsOfService.section10.list', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-3 italic">{t('termsOfService.section10.note')}</p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section11.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section11.subtitle1')}</h3>
            <p className="text-gray-700">{t('termsOfService.section11.content1')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section11.subtitle2')}</h3>
            <p className="text-gray-700">{t('termsOfService.section11.content2')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section11.subtitle3')}</h3>
            <p className="text-gray-700">{t('termsOfService.section11.content3')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section11.subtitle4')}</h3>
            <p className="text-gray-700">{t('termsOfService.section11.content4')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('termsOfService.section11.subtitle5')}</h3>
            <p className="text-gray-700">{t('termsOfService.section11.content5')}</p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.section12.title')}</h2>
            <p className="text-gray-700 mb-3">{t('termsOfService.section12.content')}</p>
            <ul className="list-none text-gray-700 space-y-2">
              <li><strong>{t('termsOfService.section12.email')}</strong> {t('termsOfService.section12.emailValue')}</li>
              <li><strong>{t('termsOfService.section12.support')}</strong> {t('termsOfService.section12.supportValue')}</li>
              <li><strong>{t('termsOfService.section12.web')}</strong> {t('termsOfService.section12.webValue')}</li>
            </ul>
          </section>

          {/* Acceptance Notice */}
          <section className="bg-blue-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('termsOfService.acceptanceNotice.title')}</h2>
            <p className="text-gray-700">{t('termsOfService.acceptanceNotice.content')}</p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-600">
          <p>{t('termsOfService.footer.copyright')}</p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
